import { cn } from 'utils/styles';
import { formatCurrency } from './currency';
import type { BudgetResult, BudgetSegment, BudgetSectionKey, BudgetSegmentKey } from './types';

const SEGMENTS: Record<BudgetSegmentKey, { name: string; fill: string; text: string }> = {
  extra: { name: 'Extra', fill: 'bg-accent-grey-400', text: 'text-content-dark' },
  essentials: { name: 'Essentials', fill: 'bg-accent-green-400', text: 'text-content-dark' },
  wants: { name: 'Wants', fill: 'bg-accent-yellow-400', text: 'text-content-dark' },
  /** blue-500 with light text because blue-400 fails the required text contrast. */
  savings: { name: 'Savings', fill: 'bg-accent-blue-500', text: 'text-content-light' },
};

/** Always show all categories, even if no remainder segment appears. */
const LEGEND_KEYS: readonly BudgetSegmentKey[] = ['essentials', 'wants', 'savings', 'extra'];

const REFERENCE_KEYS: readonly BudgetSectionKey[] = ['essentials', 'wants', 'savings'];

const REFERENCE_LABEL_ID = 'budget-calculator-reference-bar';
const CURRENT_LABEL_ID = 'budget-calculator-current-bar';

const referenceSegments = ({ income, reference }: BudgetResult): BudgetSegment[] =>
  REFERENCE_KEYS.map(key => {
    const percentOfIncome = Math.round((reference[key] / income) * 100);

    return { key, amount: reference[key], percentOfIncome, isOver: false, basis: percentOfIncome };
  });

interface BudgetBarProps {
  labelId: string;
  label: string;
  segments: readonly BudgetSegment[];
}

const BudgetBar = ({ labelId, label, segments }: BudgetBarProps) => (
  <div className="flex flex-col gap-2">
    <p id={labelId} className="text-label-saans">
      {label}
    </p>

    {/* Keep each label, amount, and percentage readable in the fixed-height track. */}
    <ul aria-labelledby={labelId} className="flex h-60 w-[100px] flex-col overflow-hidden rounded-md sm:w-[150px]">
      {segments.map(segment => (
        <li
          key={segment.key}
          style={{ flexBasis: `${segment.basis}%` }}
          className={cn(
            'flex min-h-14 flex-col items-center justify-center px-1 text-center text-legal',
            SEGMENTS[segment.key].fill,
            SEGMENTS[segment.key].text,
          )}>
          <span>{SEGMENTS[segment.key].name}</span>
          <span>{formatCurrency(segment.amount)}</span>
          {/* Color alone cannot indicate an over-target category; avoid wording that reads as a percentage delta. */}
          <span
            className={cn(
              'flex items-center gap-1 rounded-full px-2',
              segment.isOver && 'bg-util-alert-500 text-content-light',
            )}>
            {segment.isOver && <span aria-hidden>!</span>}
            <span>{segment.percentOfIncome}%</span>
            {segment.isOver && <span className="sr-only">above target</span>}
          </span>
        </li>
      ))}
    </ul>
  </div>
);

interface BudgetBarGraphProps {
  result: BudgetResult;
}

export const BudgetBarGraph = ({ result }: BudgetBarGraphProps) => (
  <div className="flex flex-col gap-4">
    {/* Hidden from assistive tech: it maps colors to names, which every segment already states in text. */}
    <ul aria-hidden className="flex flex-wrap gap-x-4 gap-y-2">
      {LEGEND_KEYS.map(key => (
        <li key={key} className="flex items-center gap-2 text-legal">
          <span className={cn('size-3 shrink-0 rounded-full', SEGMENTS[key].fill)} />
          {SEGMENTS[key].name}
        </li>
      ))}
    </ul>

    <div className="flex gap-6 sm:gap-10">
      <BudgetBar labelId={REFERENCE_LABEL_ID} label="50/30/20 budget" segments={referenceSegments(result)} />
      <BudgetBar labelId={CURRENT_LABEL_ID} label="Current budget" segments={result.segments} />
    </div>
  </div>
);
